import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '../../lib/api-client';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';

interface StatsResponse {
  items: Array<{
    deposit: number;
    topup: number;
    withdraw: number;
    settle: number;
    feeSeller: number;
  }>;
  total: number;
  payinFeePercent: number;
  payoutFeePercent: number;
}

export function SellerDashboard() {
  const { t } = useTranslation();

  const { data: statsData } = useQuery({
    queryKey: ['seller', 'stats'],
    queryFn: () =>
      apiGet<StatsResponse>('/seller/stats', { pageSize: '100' }),
  });

  const { data: subSellerData } = useQuery({
    queryKey: ['seller', 'sub-sellers'],
    queryFn: () =>
      apiGet<{ items: unknown[]; total: number }>('/seller/sub-sellers'),
  });

  const stats = statsData?.ok ? statsData.data.items : [];
  const payinPct = statsData?.ok ? statsData.data.payinFeePercent : 0;
  const payoutPct = statsData?.ok ? statsData.data.payoutFeePercent : 0;
  const subSellerCount = subSellerData?.ok ? subSellerData.data.total : 0;

  const totalDeposit = stats.reduce((sum, s) => sum + s.deposit, 0);
  const totalTopup = stats.reduce((sum, s) => sum + s.topup, 0);
  const totalWithdraw = stats.reduce((sum, s) => sum + s.withdraw, 0);
  const totalSettle = stats.reduce((sum, s) => sum + s.settle, 0);
  const totalFee = stats.reduce((sum, s) => sum + s.feeSeller, 0);
  const payinFeeAmount = (totalDeposit + totalTopup) * payinPct / 100;
  const payoutFeeAmount = (totalWithdraw + totalSettle) * payoutPct / 100;

  const formatNum = (n: number) =>
    n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('dashboard.title')}</h1>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader>
            <CardTitle>{t('dashboard.totalDeposits')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{formatNum(totalDeposit)}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('dashboard.totalTopups')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{formatNum(totalTopup)}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('dashboard.totalWithdraws')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">{formatNum(totalWithdraw)}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('dashboard.totalSettles')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-600">{formatNum(totalSettle)}</div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle>{t('transactions.calcPayinFee')} ({payinPct}%)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{formatNum(payinFeeAmount)}</div>
            <div className="mt-1 text-xs text-gray-500">({t('transactions.deposit')} + {t('transactions.topup')}) × {payinPct}%</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('transactions.calcPayoutFee')} ({payoutPct}%)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">{formatNum(payoutFeeAmount)}</div>
            <div className="mt-1 text-xs text-gray-500">({t('transactions.withdraw')} + {t('transactions.settle')}) × {payoutPct}%</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>{t('dashboard.totalFees')}</CardTitle>
              <span className="text-sm text-gray-500">{t('seller.subSellerCount')}: {subSellerCount}</span>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-primary">{formatNum(totalFee)}</div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
